import { io } from "socket.io-client";
import { CHAT_SOCKET_NAMESPACE, CHAT_SOCKET_URL } from "../constants/api";
import {
  attachChatSocketDebug,
  logChatSocket,
  syncChatSocketDebugFromUrl,
} from "./chatSocketDebug";

let socket = null;

/**
 * Connect as guest. Handshake query: hostId, qrCode, unitName.
 * guestId (when known) is also sent so reconnect can resume the same identity.
 */
export const connectChatSocket = ({
  hostId,
  qrCode,
  unitName,
  guestId,
} = {}) => {
  syncChatSocketDebugFromUrl();

  if (socket?.connected) {
    return socket;
  }

  if (socket) {
    socket.removeAllListeners();
    socket.disconnect();
    socket = null;
  }

  const url = `${CHAT_SOCKET_URL}${CHAT_SOCKET_NAMESPACE}`;
  const context = {
    url,
    hostId: hostId != null ? String(hostId) : "",
    qrCode: qrCode || "",
    unitName: unitName || "",
    guestId: guestId != null ? String(guestId) : null,
  };

  logChatSocket({
    direction: "sys",
    event: "connecting",
    payload: context,
  });

  socket = io(url, {
    transports: ["websocket", "polling"],
    autoConnect: true,
    // No silent reconnect — dropped sockets look like a new guest to the
    // backend. UI asks the user to reload instead.
    reconnection: false,
    query: {
      hostId: context.hostId,
      qrCode: context.qrCode,
      unitName: context.unitName,
      ...(guestId ? { guestId: String(guestId) } : {}),
    },
    auth: {
      hostId: context.hostId,
      qrCode: context.qrCode,
      unitName: context.unitName,
      ...(guestId ? { guestId: String(guestId) } : {}),
    },
  });

  attachChatSocketDebug(socket, context);

  return socket;
};

export const getChatSocket = () => socket;

export const disconnectChatSocket = () => {
  if (!socket) return;
  logChatSocket({
    direction: "sys",
    event: "client_disconnect",
    payload: {
      socketId: socket.id || null,
      wasConnected: Boolean(socket.connected),
    },
  });
  socket.removeAllListeners();
  socket.disconnect();
  socket = null;
};

export const emitChat = (event, payload) => {
  if (!socket?.connected) {
    logChatSocket({
      direction: "out",
      event,
      payload,
      meta: { ok: false, reason: "not_connected" },
    });
    return false;
  }
  // Outbound is also logged by the socket.emit wrap in attachChatSocketDebug
  socket.emit(event, payload);
  return true;
};
